/**
 * Simplified RAF-based frame pacing meter
 * Tracks FPS metrics with minimal overhead
 */
export class RafJankMeter {
    constructor(opts) {
        this.rafId = null;
        this.previousTimestamp = null;
        this.isRunning = false;
        this.wasRunningBeforeHidden = false; // Track state before hiding
        // Welford accumulators for running statistics
        this.count = 0;
        this.mean = 0;
        this.m2 = 0;
        // Basic metrics
        this.maxFrameDeltaMs = 0;
        this.jankCount = 0;
        this.JANK_THRESHOLD_MS = opts?.jankThresholdMs ?? 16.67;
        this.MAX_FRAME_DELTA_MS = opts?.maxFrameDeltaMs ?? 1000;
        this.PAUSE_ON_HIDDEN = opts?.pauseOnHidden ?? false;
        this.boundFrameCallback = this.frameCallback.bind(this);
        // Setup visibility handling once in constructor
        if (this.PAUSE_ON_HIDDEN && typeof document !== 'undefined') {
            this.visibilityHandler = () => {
                try {
                    if (document.hidden) {
                        // Save running state before stopping
                        this.wasRunningBeforeHidden = this.isRunning;
                        if (this.isRunning) {
                            this.stop();
                        }
                    }
                    else {
                        // Restore running state when visible again
                        if (this.wasRunningBeforeHidden) {
                            this.start();
                        }
                    }
                }
                catch (e) {
                    console.error('RafJankMeter visibility handler error:', e);
                }
            };
            // Register listener once here, not in start()
            document.addEventListener('visibilitychange', this.visibilityHandler, { passive: true });
        }
    }
    /** Start RAF tracking */
    start() {
        if (this.isRunning)
            return;
        if (typeof requestAnimationFrame === 'undefined') {
            console.warn('RafJankMeter: requestAnimationFrame not supported');
            return;
        }
        this.isRunning = true;
        this.previousTimestamp = null; // Reset timestamp for clean start
        try {
            this.rafId = requestAnimationFrame(this.boundFrameCallback);
        }
        catch (e) {
            console.error('RafJankMeter: Failed to start RAF:', e);
            this.isRunning = false;
        }
        // Note: visibilitychange listener already registered in constructor
    }
    /** Stop RAF tracking */
    stop() {
        if (!this.isRunning)
            return;
        this.isRunning = false;
        if (this.rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
            try {
                cancelAnimationFrame(this.rafId);
            }
            catch (e) {
                console.error('RafJankMeter: Failed to cancel RAF:', e);
            }
            this.rafId = null;
        }
        // Don't reset wasRunningBeforeHidden here - it's managed by visibility handler
    }
    /** Cleanup resources */
    destroy() {
        this.stop();
        this.wasRunningBeforeHidden = false; // Reset state
        // Remove visibility listener if it was registered
        if (this.visibilityHandler && typeof document !== 'undefined') {
            try {
                document.removeEventListener('visibilitychange', this.visibilityHandler);
            }
            catch (e) {
                console.error('RafJankMeter: Failed to remove listener:', e);
            }
            this.visibilityHandler = undefined;
        }
        // Clear all references
        this.boundFrameCallback = null;
    }
    /** Get current metrics snapshot */
    getCurrentMetrics() {
        const stdDev = this.count > 1 ? Math.sqrt(this.m2 / (this.count - 1)) : 0;
        return {
            // Only metrics actually used in metrics-service.ts
            avgHz: this.mean > 0 ? 1000 / this.mean : 0,
            stdDevFrameDeltaMs: stdDev,
            jankCount: this.jankCount,
            maxFrameDeltaMs: this.maxFrameDeltaMs || 0
        };
    }
    /* -------------------- Internal -------------------- */
    frameCallback(timestamp) {
        if (!this.isRunning)
            return;
        if (this.previousTimestamp === null) {
            this.previousTimestamp = timestamp;
            this.rafId = requestAnimationFrame(this.boundFrameCallback);
            return;
        }
        const delta = timestamp - this.previousTimestamp;
        this.previousTimestamp = timestamp;
        // Skip background spikes
        if (delta > this.MAX_FRAME_DELTA_MS) {
            this.rafId = requestAnimationFrame(this.boundFrameCallback);
            return;
        }
        // Update Welford statistics
        this.count++;
        const deltaMean = delta - this.mean;
        this.mean += deltaMean / this.count;
        const deltaMean2 = delta - this.mean;
        this.m2 += deltaMean * deltaMean2;
        // Update basic metrics
        if (delta > this.maxFrameDeltaMs) {
            this.maxFrameDeltaMs = delta;
        }
        if (delta > this.JANK_THRESHOLD_MS) {
            this.jankCount++;
        }
        this.rafId = requestAnimationFrame(this.boundFrameCallback);
    }
}
